import './styles.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';
import { Stack } from '@edx/paragon';
import {
  FormattedMessage,
} from 'gatsby-plugin-react-intl';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import { setOnboardingComponentSignInOpen, setOnboardingComponentSignUpOpen } from '@data/actions/onboardingComponent';
import useInitOnboardingComponentExperiment from '@prospectus/common/hooks/useInitOnboardingComponentExperiment';

function MobileUserActionLinks({
  userLoggedIn,
  dashboardLinkClick,
  dashboardLink,
  loginLink,
  registerLink,
}) {
  const dispatch = useDispatch();
  const registerButtonRef = useRef(null);
  const loginButtonRef = useRef(null);
  const [isRegisterButtonTracked, setIsRegisterButtonTracked] = useState(false);
  const [isLoginButtonTracked, setIsLoginButtonTracked] = useState(false);
  const [buttonClickedName, setButtonClickedName] = useState('');

  const {
    isUserInOnboardingExperimentVariation, isOnboardingComponentExperimentLoaded,
  } = useInitOnboardingComponentExperiment(!!buttonClickedName);

  useEffect(() => {
    if (buttonClickedName && isOnboardingComponentExperimentLoaded) {
      const buttonRef = buttonClickedName === 'register' ? registerButtonRef : loginButtonRef;
      const isButtonTracked = buttonClickedName === 'register' ? isRegisterButtonTracked : isLoginButtonTracked;

      if (isUserInOnboardingExperimentVariation || (!isUserInOnboardingExperimentVariation && isButtonTracked)) {
        buttonRef.current.click();
      }
    }
  }, [
    isOnboardingComponentExperimentLoaded,
    isRegisterButtonTracked,
    isLoginButtonTracked,
  ]);

  const handleRegisterClick = (e) => {
    if (!isOnboardingComponentExperimentLoaded) {
      e.preventDefault();
      // manually trigger experiment
      setButtonClickedName('register');
    } else if (isOnboardingComponentExperimentLoaded && isUserInOnboardingExperimentVariation) {
      e.preventDefault();
      dispatch(setOnboardingComponentSignUpOpen());
      window.analytics.track('edx.bi.user.register.clicked', {
        category: 'navigation',
        link: registerButtonRef.current.getAttribute('href'),
      });
    }
  };

  const handleSignInClick = (e) => {
    if (!isOnboardingComponentExperimentLoaded) {
      e.preventDefault();
      // manually trigger experiment
      setButtonClickedName('login');
    } else if (isOnboardingComponentExperimentLoaded && isUserInOnboardingExperimentVariation) {
      e.preventDefault();
      dispatch(setOnboardingComponentSignInOpen());
      window.analytics.track('edx.bi.user.login.clicked', {
        category: 'navigation',
        link: loginButtonRef.current.getAttribute('href'),
      });
    }
  };

  if (userLoggedIn) {
    return (
      <div className="p-3 border-bottom bg-white">
        <li className="menu-link visible-mobile user-cta d-flex" key="dashboard">
          <a
            href={dashboardLink}
            className="btn btn-primary w-100"
            onClick={dashboardLinkClick}
          >
            <FormattedMessage
              id="prospectus.header.site_nav.user_menu.dashboard"
              defaultMessage="Dashboard"
              description="Dashboard link text in the user portion of the menu - when the user is logged in"
            />
          </a>
        </li>
      </div>
    );
  }

  return (
    <div className="p-3 border-bottom bg-white">
      <li className="menu-link visible-mobile inline-primary-ctas d-flex justify-content-between" key="login">
        <Stack gap={2} direction="horizontal" className="w-100">
          <ProspectusLink
            linkRef={loginButtonRef}
            href={loginLink}
            className="btn btn-outline-brand bg-transparent w-50 account-access-button"
            onClick={handleSignInClick}
            onTrackingAdded={() => setTimeout(() => { setIsLoginButtonTracked(true); }, 300)}
            isTrackLink={isOnboardingComponentExperimentLoaded && !isUserInOnboardingExperimentVariation}
            analytics={{
              eventName: 'edx.bi.user.login.clicked',
            }}
          >
            <FormattedMessage
              id="prospectus.header.site_nav.user_menu.sign_in"
              defaultMessage="Sign In"
              description="Sign In link text in the user portion of the menu - when the user is logged out"
            />
          </ProspectusLink>
          <ProspectusLink
            linkRef={registerButtonRef}
            href={registerLink}
            className="btn btn-brand w-50 account-access-button"
            onClick={handleRegisterClick}
            onTrackingAdded={() => setTimeout(() => { setIsRegisterButtonTracked(true); }, 300)}
            isTrackLink={isOnboardingComponentExperimentLoaded && !isUserInOnboardingExperimentVariation}
            analytics={{
              eventName: 'edx.bi.user.register.clicked',
            }}
          >
            <FormattedMessage
              id="prospectus.header.site_nav.user_menu.register.for.free"
              defaultMessage="Register for free"
              description="Text for a link that will lead the user to our registration flow"
            />
          </ProspectusLink>
        </Stack>
      </li>
    </div>

  );
}

MobileUserActionLinks.propTypes = {
  userLoggedIn: PropTypes.bool.isRequired,
  dashboardLinkClick: PropTypes.func.isRequired,
  loginLink: PropTypes.string.isRequired,
  registerLink: PropTypes.string.isRequired,
  dashboardLink: PropTypes.string.isRequired,
};

export default MobileUserActionLinks;
